import * as personManagementApi from '@api/person-management';
import { useRecentGetter } from '@lib/common';
import {
  attributeOptionsFromMaster,
  ClassificationField,
  ClassificationOption,
} from '@lib/person-management';
import { Fab, TextField } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import FilterIcon from '@material-ui/icons/FilterList';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { $eq } from '../operators';

let catalogRequest: Promise<Record<ClassificationField, ClassificationOption[]>> | null = null;

function loadCatalog() {
  if (!catalogRequest) {
    catalogRequest = personManagementApi
      .searchAttributeMaster(1, 100)
      .toPromise()
      .then(response => attributeOptionsFromMaster(response))
      .catch(error => {
        catalogRequest = null;
        throw error;
      });
  }
  return catalogRequest;
}

export function AttributeFilterChip({
  label,
  value,
  field,
}: {
  label?: React.ReactNode;
  value?: any;
  field: ClassificationField;
}) {
  const [text, setText] = useState(String(value ?? ''));

  useEffect(() => {
    let active = true;
    loadCatalog()
      .then(groups => {
        if (!active) return;
        const match = (groups[field] || []).find(option => option.id === value);
        if (match) setText(match.label);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [value, field]);

  return (
    <>
      {label}: {text}
    </>
  );
}

export function withAttributeSelect(field: ClassificationField, hideDeprecated = false) {
  return function AttributeSelectFilter(props) {
    const { handleSubmit, ...form } = useForm();
    const {
      column: { filterValue, setFilter },
      closeFilter,
    } = props;
    const getFilterValue = useRecentGetter(filterValue);
    const getFilterSetter = useRecentGetter(setFilter);
    const getCloseFilter = useRecentGetter(closeFilter);
    const [options, setOptions] = useState<ClassificationOption[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      let active = true;
      loadCatalog()
        .then(groups => {
          if (!active) return;
          const next = groups[field] || [];
          setOptions(hideDeprecated ? next.filter(option => !option.deprecated) : next);
        })
        .catch(() => {
          if (active) setOptions([]);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
      return () => {
        active = false;
      };
    }, []);

    const submit = useMemo(
      () =>
        handleSubmit(values => {
          const toSet = { ...getFilterValue() };
          if (values.value) {
            toSet[$eq.key] = values.value;
            getFilterSetter()(toSet);
          }
          getCloseFilter()();
        }),
      [],
    );

    return (
      <form onSubmit={submit} style={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
        <div style={{ position: 'relative', minWidth: 180 }}>
          <TextField
            inputRef={form.register}
            label="Select"
            variant="outlined"
            select
            size="small"
            disabled={loading}
            SelectProps={{ native: true }}
            inputProps={{ style: { paddingRight: loading ? 56 : 32 } }}
            name="value"
            style={{ minWidth: 180 }}
          >
            <option value="">Select</option>
            {options.map(option => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </TextField>
          {loading ? (
            <CircularProgress
              size={16}
              style={{ position: 'absolute', right: 32, top: '50%', marginTop: -8 }}
            />
          ) : null}
        </div>
        &nbsp;&nbsp;&nbsp;&nbsp;
        <Fab color="primary" size="small" type="submit" disabled={loading}>
          <FilterIcon />
        </Fab>
      </form>
    );
  };
}
